// ============================================
// FAMUZA MARKET — Homepage v2.0
// Cayden + Claude mobile-first redesign
// Products visible within 1 second on mobile
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { supabase } from '../supabase';

const CACHE_KEY = 'famuza_home_v2';
const CACHE_DURATION = 5 * 60 * 1000;

const CATEGORIES = [
  { name: 'All',          emoji: '🛍️',  path: '/listings' },
  { name: 'Deals',        emoji: '🔥',  path: '/listings?deals=true' },
  { name: 'Shoes',        emoji: '👟',  path: '/listings?cat=Shoes' },
  { name: 'Fashion',      emoji: '👗',  path: '/listings?cat=Salaula' },
  { name: 'Electronics',  emoji: '📱',  path: '/listings?cat=Electronics' },
  { name: 'Furniture',    emoji: '🛋️',  path: '/listings?cat=Furniture — Wayfair UK' },
  { name: 'Baby & Kids',  emoji: '👶',  path: '/listings?cat=Baby Products' },
  { name: 'Toys',         emoji: '🧸',  path: '/listings?cat=Used Toys' },
  { name: 'Beauty',       emoji: '💄',  path: '/listings?cat=Beauty & Cosmetics' },
  { name: 'Home',         emoji: '🏠',  path: '/listings?cat=Home Essentials' },
  { name: 'Bags',         emoji: '👜',  path: '/listings?cat=Accessories & Handbags' },
  { name: 'ASDA Returns', emoji: '📦',  path: '/listings?cat=Returns — ASDA/George' },
  { name: 'Amazon UK',    emoji: '🇬🇧', path: '/listings?cat=Returns — Amazon UK' },
  { name: 'Fitness',      emoji: '💪',  path: '/listings?cat=Fitness & Gym' },
  { name: 'Power',        emoji: '⚡',  path: '/listings?cat=Power & Solar' },
  { name: 'Farming',      emoji: '🌾',  path: '/listings?cat=Farming & Tools' },
];

const WaIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.845L0 24l6.335-1.508A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.366l-.36-.213-3.724.977.994-3.634-.234-.374A9.818 9.818 0 1112 21.818z"/>
  </svg>
);

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 769);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr);
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

// ── Unified Product Card (used everywhere) ────────────────────────────────────
function ProductCard({ item, navigate, formatPrice, whatsAppEnquiry, badge }) {
  const discount = item.old_price
    ? Math.round(((Number(item.old_price) - Number(item.price)) / Number(item.old_price)) * 100)
    : null;
  const isNew = (Date.now() - new Date(item.created_at)) < 48 * 3600000;

  return (
    <div
      onClick={() => navigate(`/product/${item.id}`)}
      style={{ background: '#1E1E1E', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', flexShrink: 0, transition: 'transform 0.18s, box-shadow 0.18s', boxShadow: '0 2px 12px rgba(0,0,0,0.4)' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.6)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.4)'; }}
    >
      {/* Image — 80% height ratio */}
      <div style={{ position: 'relative', paddingBottom: '80%', background: '#2A2A2A', overflow: 'hidden' }}>
        <img
          src={item.image_url} alt={item.title}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.35s' }}
          onError={e => { e.target.style.display = 'none'; }}
          onMouseEnter={e => e.target.style.transform = 'scale(1.06)'}
          onMouseLeave={e => e.target.style.transform = 'scale(1)'}
        />
        {/* Badges — top left stack */}
        <div style={{ position: 'absolute', top: 7, left: 7, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {discount && (
            <span style={{ background: '#E8622A', color: '#fff', fontSize: '0.65rem', fontWeight: 800, padding: '3px 8px', borderRadius: 100, boxShadow: '0 2px 8px rgba(232,98,42,0.6)' }}>
              -{discount}%
            </span>
          )}
          {isNew && (
            <span style={{ background: '#25D366', color: '#fff', fontSize: '0.65rem', fontWeight: 800, padding: '3px 8px', borderRadius: 100, boxShadow: '0 2px 8px rgba(37,211,102,0.5)' }}>
              🔥 NEW
            </span>
          )}
          {badge && (
            <span style={{ background: 'rgba(0,0,0,0.8)', color: '#F5C842', fontSize: '0.6rem', fontWeight: 700, padding: '3px 8px', borderRadius: 100, border: '0.5px solid rgba(245,200,66,0.35)' }}>
              {badge}
            </span>
          )}
        </div>
        {(item.stock === 1 || item.quantity === 1) && (
          <div style={{ position: 'absolute', bottom: 7, left: 7, background: 'rgba(0,0,0,0.85)', color: '#ff6b6b', fontSize: '0.6rem', fontWeight: 700, padding: '3px 8px', borderRadius: 100 }}>
            🔴 Only 1 left!
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '0.7rem 0.7rem 0.6rem' }}>
        {/* Price — FIRST, BIGGEST, ORANGE */}
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.2rem', fontWeight: 800, color: '#E8622A', lineHeight: 1, marginBottom: 4 }}>
          {formatPrice(item.price)}
          {item.old_price && (
            <span style={{ fontSize: '0.65rem', color: 'rgba(245,240,232,0.3)', textDecoration: 'line-through', marginLeft: 6, fontWeight: 400 }}>
              {formatPrice(item.old_price)}
            </span>
          )}
        </div>

        {/* Title — 1 line */}
        <div style={{ fontSize: '0.78rem', fontWeight: 500, color: 'rgba(245,240,232,0.9)', lineHeight: 1.3, marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.title}
        </div>

        {/* Bottom row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
          <div style={{ fontSize: '0.6rem', color: 'rgba(245,240,232,0.35)', lineHeight: 1.5 }}>
            {item.location && <div>📍 {item.location}</div>}
            <div>🕐 {timeAgo(item.created_at)} · {item.views || 0} views</div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); whatsAppEnquiry(item, item.seller_phone); }}
            style={{ width: 34, height: 34, borderRadius: '50%', background: '#25D366', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: '0 3px 12px rgba(37,211,102,0.45)' }}>
            <WaIcon size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{ background: '#1A1A1A', borderRadius: 14, overflow: 'hidden', flexShrink: 0 }}>
      <div style={{ paddingBottom: '100%', background: 'linear-gradient(90deg, #1E1E1E 25%, #2A2A2A 50%, #1E1E1E 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
      <div style={{ padding: '0.6rem' }}>
        <div style={{ height: 7, background: '#2A2A2A', borderRadius: 4, marginBottom: 6, width: '50%' }} />
        <div style={{ height: 9, background: '#2A2A2A', borderRadius: 4, marginBottom: 4 }} />
        <div style={{ height: 9, background: '#2A2A2A', borderRadius: 4, marginBottom: 8, width: '70%' }} />
        <div style={{ height: 16, background: '#2A2A2A', borderRadius: 4, width: '45%' }} />
      </div>
    </div>
  );
}

function Section({ title, emoji, items, loading, navigate, formatPrice, whatsAppEnquiry, link, getBadge }) {
  const isMobile = useIsMobile();
  const skeletons = [1, 2, 3, 4, 5, 6];

  if (!loading && items.length === 0) return null;

  return (
    <div style={{ padding: '0 0 1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1rem', marginBottom: '0.75rem' }}>
        <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 800, color: '#F5F0E8' }}>
          {emoji} <span style={{ color: '#E8622A' }}>{title}</span>
        </span>
        <Link to={link || '/listings'} style={{ fontSize: '0.75rem', color: '#E8622A', textDecoration: 'none', fontWeight: 600 }}>
          See all →
        </Link>
      </div>

      {isMobile ? (
        <div style={{ display: 'flex', overflowX: 'auto', gap: '0.75rem', padding: '0 1rem 0.25rem', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {loading
            ? skeletons.map(i => <div key={i} style={{ flexShrink: 0, width: 148 }}><SkeletonCard /></div>)
            : items.map(item => (
                <div key={item.id} style={{ flexShrink: 0, width: 148 }}>
                  <ProductCard item={item} navigate={navigate} formatPrice={formatPrice} whatsAppEnquiry={whatsAppEnquiry} badge={getBadge ? getBadge(item) : null} />
                </div>
              ))
          }
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.75rem', padding: '0 1rem' }}>
          {loading
            ? skeletons.map(i => <SkeletonCard key={i} />)
            : items.map(item => (
                <ProductCard key={item.id} item={item} navigate={navigate} formatPrice={formatPrice} whatsAppEnquiry={whatsAppEnquiry} badge={getBadge ? getBadge(item) : null} />
              ))
          }
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const { whatsAppEnquiry, formatPrice, cart } = useApp();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [deals, setDeals] = useState([]);
  const [hot, setHot] = useState([]);
  const [recent, setRecent] = useState([]);
  const [fresh, setFresh] = useState([]);
  const [loading, setLoading] = useState(true);

  const cartCount = cart ? cart.reduce((sum, item) => sum + (item.qty || 1), 0) : 0;

  useEffect(() => {
    async function fetchHomeProducts() {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            setHot(data.hot || []);
            setRecent(data.recent || []);
            setDeals(data.deals || []);
            setFresh(data.fresh || []);
            setLoading(false);
            return;
          }
        }
      } catch (e) {}

      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(60);

      if (error) { setLoading(false); return; }

      const hotProducts = [...data].filter(p => (p.views || 0) > 0).sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 8);
      const recentProducts = data.filter(p => (Date.now() - new Date(p.created_at)) < 7 * 86400000).slice(0, 8);
      const dealProducts = data.filter(p => p.old_price).slice(0, 8);
      const freshProducts = data.slice(0, 8);

      setHot(hotProducts);
      setRecent(recentProducts);
      setDeals(dealProducts);
      setFresh(freshProducts);
      setLoading(false);

      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: { hot: hotProducts, recent: recentProducts, deals: dealProducts, fresh: freshProducts },
          timestamp: Date.now(),
        }));
      } catch (e) {}
    }
    fetchHomeProducts();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/listings?search=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh', overflowX: 'hidden' }}>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        ::-webkit-scrollbar { display: none; }
        * { -webkit-tap-highlight-color: transparent; }
      `}</style>

      {/* ── TOP NAV BAR ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: '#111', borderBottom: '0.5px solid rgba(255,255,255,0.07)' }}>
        <div style={{ padding: '0.7rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.2rem', fontWeight: 800, color: '#F5F0E8', letterSpacing: '-0.02em' }}>
              FAMUZA <span style={{ color: '#E8622A' }}>Market</span>
            </div>
          </Link>

          {/* Search — desktop inline */}
          {!isMobile && (
            <form onSubmit={handleSearch} style={{ flex: 1, maxWidth: 500, display: 'flex', background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 100, overflow: 'hidden' }}>
              <span style={{ padding: '0 0.75rem', display: 'flex', alignItems: 'center', color: 'rgba(245,240,232,0.4)' }}>🔍</span>
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search phones, shoes, furniture..."
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#F5F0E8', fontSize: '0.88rem', padding: '0.6rem 0', fontFamily: 'DM Sans, sans-serif' }}
              />
              <button type="submit" style={{ background: '#E8622A', color: '#fff', border: 'none', padding: '0 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                Search
              </button>
            </form>
          )}

          {/* Right icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
            <Link to="/seller/auth" style={{ textDecoration: 'none', fontSize: '0.75rem', color: 'rgba(245,240,232,0.6)', display: isMobile ? 'none' : 'block' }}>
              Sell
            </Link>
            <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => navigate('/cart')}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(245,240,232,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              {cartCount > 0 && (
                <div style={{ position: 'absolute', top: -6, right: -6, background: '#E8622A', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: '0.58rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {cartCount}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile search bar */}
        {isMobile && (
          <form onSubmit={handleSearch} style={{ padding: '0 1rem 0.6rem', display: 'flex', background: '#1E1E1E', margin: '0 1rem 0.6rem', borderRadius: 100, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
            <span style={{ padding: '0 0.75rem', display: 'flex', alignItems: 'center', color: 'rgba(245,240,232,0.4)', flexShrink: 0 }}>🔍</span>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search phones, shoes, furniture..."
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#F5F0E8', fontSize: '0.88rem', padding: '0.6rem 0', fontFamily: 'DM Sans, sans-serif' }}
            />
          </form>
        )}
      </div>

      {/* ── CATEGORIES SCROLL ── */}
      <div style={{ background: '#111', borderBottom: '0.5px solid rgba(255,255,255,0.06)', padding: '0.6rem 0' }}>
        <div style={{ display: 'flex', overflowX: 'auto', gap: '0.3rem', padding: '0 1rem', scrollbarWidth: 'none' }}>
          {CATEGORIES.map(cat => (
            <Link key={cat.name} to={cat.path} style={{ textDecoration: 'none', flexShrink: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem', padding: '0.3rem 0.6rem', borderRadius: 10, background: 'rgba(255,255,255,0.04)', minWidth: 52, cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(232,98,42,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}>
                <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>{cat.emoji}</span>
                <span style={{ fontSize: '0.55rem', color: 'rgba(245,240,232,0.65)', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>{cat.name}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* ── DESKTOP HERO (mobile: hidden) ── */}
      {!isMobile && (
        <div style={{ background: 'linear-gradient(135deg, #1A0A04 0%, #0D0D0D 60%)', padding: '2rem 2rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2rem' }}>
          <div style={{ maxWidth: 480 }}>
            <div style={{ fontSize: '0.72rem', color: '#C49A2A', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '0.6rem' }}>UNIFAM · OFFICIAL MARKETPLACE</div>
            <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '2.5rem', fontWeight: 800, color: '#F5F0E8', lineHeight: 1.05, margin: '0 0 0.75rem' }}>
              Buy & Sell with <span style={{ color: '#E8622A' }}>Real</span> People
            </h1>
            <p style={{ fontSize: '0.95rem', color: 'rgba(245,240,232,0.55)', margin: '0 0 1.25rem', lineHeight: 1.7 }}>
              Trusted deals across Lusaka — shoes, electronics, salaula & more. Pay with MTN MoMo or Cash.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Link to="/listings">
                <button style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  Browse listings →
                </button>
              </Link>
              <Link to="/seller/auth">
                <button style={{ background: 'transparent', color: '#F5F0E8', border: '0.5px solid rgba(245,240,232,0.2)', borderRadius: 100, padding: '0.75rem 1.5rem', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  Start selling free
                </button>
              </Link>
            </div>
          </div>
          {/* Mini product grid preview */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', width: 280, flexShrink: 0 }}>
            {(loading ? [1,2,3,4] : hot.slice(0,4)).map((item, i) =>
              loading ? (
                <div key={i} style={{ background: '#1A1A1A', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ height: 90, background: 'linear-gradient(90deg,#1E1E1E 25%,#2A2A2A 50%,#1E1E1E 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
                  <div style={{ padding: '0.4rem' }}><div style={{ height: 8, background: '#2A2A2A', borderRadius: 4 }} /></div>
                </div>
              ) : (
                <div key={item.id} onClick={() => navigate(`/product/${item.id}`)} style={{ background: '#1A1A1A', borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}>
                  <div style={{ height: 90, overflow: 'hidden', background: '#242424' }}>
                    <img src={item.image_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
                  </div>
                  <div style={{ padding: '0.4rem' }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.78rem', fontWeight: 800, color: '#F5C842' }}>{formatPrice(item.price)}</div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* ── PRODUCT SECTIONS ── */}
      <div style={{ paddingTop: '1rem' }}>

        <Section title="Hot Right Now" emoji="🔥" items={hot} loading={loading}
          navigate={navigate} formatPrice={formatPrice} whatsAppEnquiry={whatsAppEnquiry}
          link="/listings"
          getBadge={item => item.views > 0 ? `${item.views} views` : null}
        />

        <Section title="Recently Added" emoji="⚡" items={recent} loading={loading}
          navigate={navigate} formatPrice={formatPrice} whatsAppEnquiry={whatsAppEnquiry}
          link="/listings"
          getBadge={item => {
            const hrs = Math.floor((Date.now() - new Date(item.created_at)) / 3600000);
            return hrs < 24 ? `${hrs}h ago` : null;
          }}
        />

        <Section title="Deals" emoji="💰" items={deals} loading={loading}
          navigate={navigate} formatPrice={formatPrice} whatsAppEnquiry={whatsAppEnquiry}
          link="/listings?deals=true"
        />

        <Section title="Fresh Listings" emoji="👑" items={fresh} loading={loading}
          navigate={navigate} formatPrice={formatPrice} whatsAppEnquiry={whatsAppEnquiry}
          link="/listings"
        />
      </div>

      {/* ── SELLER CTA — after all products, compact ── */}
      <div style={{ padding: '0 1rem 3rem' }}>
        <div style={{ background: '#161616', border: '0.5px solid rgba(232,98,42,0.15)', borderRadius: 14, padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.9rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.2rem' }}>Start selling free</div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)' }}>List in under 60 seconds. No fees.</div>
          </div>
          <Link to="/seller/auth" style={{ flexShrink: 0 }}>
            <button style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' }}>
              Sell now →
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
